import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPinIcon,
  ClockIcon,
  CreditCardIcon,
  CheckCircleIcon,
  CalendarIcon,
  WalletIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useCarContext } from '../../../contexts/CarContext';
import { auth } from '../../../firebase';
import { useDriver } from '../../../contexts/DriverContext';
import InvoiceDataModal from '../modals/InvoiceDataModal';
import useApiClient from "../../../hooks/useApiClient";

const ParkingPayment = () => {
  const apiClient = useApiClient();
  const { t } = useTranslation();
  const { cars } = useCarContext();
  const { driverAccount, hasInvoiceData, refreshDriverAccount } = useDriver();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [selectedCar, setSelectedCar] = useState(null);
  const [plateNumber, setPlateNumber] = useState('');
  const [zone, setZone] = useState('');
  const [selectedZoneData, setSelectedZoneData] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('balance');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [saveForFutureUse, setSaveForFutureUse] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [parkingHistory, setParkingHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySize] = useState(5); // Fixed page size for history
  const [totalHistoryPages, setTotalHistoryPages] = useState(0);
  const [totalHistoryElements, setTotalHistoryElements] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationCity, setLocationCity] = useState('');
  const [locationStreet, setLocationStreet] = useState('');
  const [detectedZone, setDetectedZone] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [useManualCity, setUseManualCity] = useState(false);

  // API-based cities and zones
  const [availableCities, setAvailableCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [parkingZones, setParkingZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  // Get user location on component mount
  useEffect(() => {
    getUserLocation();
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch zones when city is selected
  useEffect(() => {
    if (selectedCity) {
      fetchZonesByCity(selectedCity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  // Auto-select city when cities are loaded and location is detected (only if not manually changed)
  useEffect(() => {
    if (locationCity && availableCities.length > 0 && !selectedCity && !useManualCity) {
      const matchingCity = availableCities.find(c =>
        c.name.toLowerCase() === locationCity.toLowerCase()
      );
      if (matchingCity) {
        setSelectedCity(matchingCity.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCity, availableCities]);

  // Fetch payment methods when payment method changes to card
  useEffect(() => {
    if (paymentMethod === 'card') {
      fetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // Fetch payment history on component mount and when page changes
  useEffect(() => {
    fetchPaymentHistory(historyPage, historySize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage]);

  // Fetch available cities
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await fetch('https://api.servicegest.ro/public/parking/cities');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        // Use the original city name as both id and name
        const cities = data.map(cityName => ({
          id: cityName,  // Keep original format for API calls
          name: cityName
        }));
        setAvailableCities(cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setAvailableCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch zones for selected city
  const fetchZonesByCity = async (cityId) => {
    setLoadingZones(true);

    try {
      const response = await fetch(`https://api.servicegest.ro/public/parking/cities/${cityId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && Array.isArray(data)) {
        // Try to match detected street with zone streets
        let matchedZone = null;
        if (locationStreet && !useManualCity) {  // Only auto-detect if not manually changed          
          // Normalize the detected street for comparison
          const normalizedStreet = locationStreet.toLowerCase().trim();

          // Try to find a zone that includes this street
          matchedZone = data.find(zone => {
            if (!zone.streets || !Array.isArray(zone.streets)) return false;

            return zone.streets.some(street => {
              const normalizedZoneStreet = street.toLowerCase().trim();
              // Check if streets match (contains or exact match)
              return normalizedStreet.includes(normalizedZoneStreet) ||
                normalizedZoneStreet.includes(normalizedStreet);
            });
          });
        }

        // Update all zone-related state at once after fetching
        setParkingZones(data);

        // Set detected zone based on manual/auto selection
        if (matchedZone && !useManualCity) {
          setDetectedZone(matchedZone.zoneName);
        } else if (useManualCity) {
          setDetectedZone(null);
        }

        // Auto-select matched zone or first zone if available
        if (data.length > 0) {
          const zoneToSelect = matchedZone || data[0];
          setZone(zoneToSelect.zoneName); // Use zoneName for grouping
          setSelectedDuration(zoneToSelect.zoneId); // Store the specific duration's zoneId
          setSelectedZoneData(zoneToSelect);
        } else {
          // Reset zone selections if no zones available
          setZone('');
          setSelectedDuration('');
          setSelectedZoneData(null);
        }
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      console.error('Error response:', error.response);
      setParkingZones([]);
      setZone('');
      setSelectedDuration('');
      setSelectedZoneData(null);
    } finally {
      setLoadingZones(false);
    }
  };

  // Fetch saved payment methods
  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const response = await fetch('https://api.servicegest.ro/driver/payment/methods', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        setPaymentMethods(data);
        // Auto-select first payment method if available
        if (data.length > 0) {
          setSelectedPaymentMethodId(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Fetch parking history with proper pagination
  const fetchPaymentHistory = async (page = 1, size = 5) => {
    setPaymentHistoryLoading(true);
    setPaymentHistoryError(null);

    try {
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const url = new URL('https://api.servicegest.ro/driver/parking/list');
      url.searchParams.append('page', page.toString());
      url.searchParams.append('size', size.toString());
      url.searchParams.append('orderAsc', 'false');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('Parking history API response:', data);

      if (data && Array.isArray(data)) {
        const parkingRecords = data;

        // Transform API data to match existing component structure
        const transformedHistory = parkingRecords.map(parking => {
          return {
            id: parking.id,
            paymentType: t('parkingPayment.parkingPayment'),
            displayDetails: `${parking.make} ${parking.type}`,
            plateNumber: parking.registrationNr,
            zone: parking.zoneName,
            zoneName: parking.zoneName,
            zoneId: parking.zoneId,
            city: parking.city,
            duration: parking.timeInMinutes / 60, // Convert minutes to hours
            timeInMinutes: parking.timeInMinutes,
            price: 0, // Price not available in this endpoint
            parkingPrice: 0,
            currency: 'RON',
            fees: {
              transaction: 0,
              stripe: 0,
              service: 0
            },
            paymentMethod: 'balance', // Default since not specified
            paymentStatus: parking.status,
            timestamp: parking.createdAt,
            expiresAt: parking.validUntil,
            carId: parking.carId,
            make: parking.make,
            type: parking.type,
            status: parking.status
          };
        });

        setParkingHistory(transformedHistory);

        // Simple pagination logic based on response size
        // If we get exactly 'size' items, assume there are more pages
        const currentHasMorePages = parkingRecords.length === size;
        const totalPages = currentHasMorePages ? page + 1 : page;
        const totalElements = parkingRecords.length; // We only know about current page

        setTotalHistoryPages(totalPages);
        setTotalHistoryElements(totalElements);
        setHasMorePages(currentHasMorePages);

        console.log('Pagination state:', {
          currentPage: page,
          totalPages,
          totalElements,
          receivedItems: parkingRecords.length,
          pageSize: size,
          hasMorePages: currentHasMorePages
        });
      } else {
        setParkingHistory([]);
        setTotalHistoryPages(0);
        setTotalHistoryElements(0);
      }
    } catch (error) {
      console.error('Error fetching parking history:', error);
      setPaymentHistoryError('Failed to load parking history');
      setParkingHistory([]);
      setTotalHistoryPages(0);
      setTotalHistoryElements(0);
    } finally {
      setPaymentHistoryLoading(false);
    }
  };
  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });

        // Get city name and street from coordinates using reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'ro'
              }
            }
          );
          const data = await response.json();

          const city = data.address?.city ||
            data.address?.town ||
            data.address?.village ||
            data.address?.municipality ||
            'Unknown';

          const street = data.address?.road ||
            data.address?.street ||
            data.address?.pedestrian ||
            '';

          setLocationCity(city);
          setLocationStreet(street);

          // Auto-select city if it matches available cities (only if not manually set)
          if (city && availableCities.length > 0 && !useManualCity) {
            const matchingCity = availableCities.find(c =>
              c.name.toLowerCase() === city.toLowerCase()
            );
            if (matchingCity) {
              setSelectedCity(matchingCity.id);
            }
          }
        } catch (error) {
          console.error('Error getting location details:', error);
          setLocationError('Could not determine location details');
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationError(error.message);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Calculate total price based on zone and payment method
  const calculatePrice = () => {
    if (!selectedDuration || parkingZones.length === 0) return 0;
    const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
    if (!selectedZone) return 0;

    // Use userPriceWithAccount for balance payment, userPrice for card payment
    const priceField = paymentMethod === 'balance' ? 'userPriceWithAccount' : 'userPrice';
    const price = selectedZone[priceField];

    if (typeof price === 'number') {
      return price;
    }
    // If string, parse it (handles "10.7" or "10,7")
    const priceStr = String(price).replace(',', '.');
    return parseFloat(priceStr) || 0;
  };

  // Get unique zone names
  const getUniqueZones = () => {
    const uniqueZones = [];
    const seenZones = new Set();

    parkingZones.forEach(zone => {
      if (!seenZones.has(zone.zoneName)) {
        seenZones.add(zone.zoneName);
        uniqueZones.push(zone);
      }
    });

    return uniqueZones;
  };

  // Get durations for selected zone
  const getDurationsForZone = (zoneName) => {
    return parkingZones.filter(z => z.zoneName === zoneName);
  };

  // Calculate processing fees for card payments
  const calculateProcessingFee = () => {
    if (!selectedDuration || parkingZones.length === 0 || paymentMethod !== 'card') return 0;

    const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
    if (!selectedZone) return 0;

    const userPrice = parseFloat(String(selectedZone.userPrice).replace(',', '.')) || 0;
    const userPriceWithAccount = parseFloat(String(selectedZone.userPriceWithAccount).replace(',', '.')) || 0;

    return userPrice - userPriceWithAccount;
  };

  // Update zone data when duration changes
  useEffect(() => {
    if (selectedDuration && parkingZones.length > 0) {
      const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
      if (selectedZone) {
        setSelectedZoneData(selectedZone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDuration, parkingZones]);

  // Handle car selection
  const handleCarSelect = (car) => {
    setSelectedCar(car);
    setPlateNumber(car.plate);
  };

  // Handle payment submission
  const handlePayment = async (e) => {
    e.preventDefault();

    // Check if invoice data is complete before processing payment
    if (!hasInvoiceData) {
      setShowInvoiceModal(true);
      return;
    }

    if (!plateNumber || !zone || !selectedCity || !selectedDuration) {
      alert(t('parkingPayment.completeFields'));
      return;
    }

    if (!selectedCar) {
      alert(t('parkingPayment.selectVehicle'));
      return;
    }

    // Check balance if paying with balance
    if (paymentMethod === 'balance') {
      const totalPrice = calculatePrice();
      if (!driverAccount || totalPrice > (driverAccount.balance || 0)) {
        alert(t('parkingPayment.insufficientBalanceMessage'));
        return;
      }
    }

    // Check payment method if paying with card
    if (paymentMethod === 'card' && paymentMethods.length > 0 && !selectedPaymentMethodId) {
      alert(t('parkingPayment.selectPaymentMethodOrAddCard'));
      return;
    }

    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();

      const selectedZone = parkingZones.find(z => z.zoneId === selectedDuration);
      if (!selectedZone) {
        throw new Error('Zone not found');
      }

      // Prepare request payload
      const payload = {
        zoneId: selectedDuration, // This is the UUID zoneId
        carId: selectedCar.id, // Use car id from selected car
        payWithBalance: paymentMethod === 'balance',
        savePaymentMethod: saveForFutureUse,
      };

      // Add payment method ID if paying with card and a method is selected
      if (paymentMethod === 'card' && selectedPaymentMethodId) {
        payload.paymentMethodId = selectedPaymentMethodId;
      }

      // Make payment request
      const response = await fetch(
        'https://api.servicegest.ro/driver/parking/payment',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        },
      );
      console.log('Payment payload:', payload);

      // Handle response based on status code
      if (response.status === 202) {
        // Success - Off-session payment completed
        setShowSuccess(true);

        // Refresh driver account balance
        if (refreshDriverAccount) {
          await refreshDriverAccount();
        }

        // Refresh payment history to show the new payment
        fetchPaymentHistory(historyPage, historySize);

        // Reset form after showing success
        setTimeout(() => {
          setShowSuccess(false);
          // Don't reset zone if it was auto-detected
          if (!detectedZone) {
            setZone('');
            setSelectedZoneData(null);
          }
          setSelectedDuration('');
        }, 3000);
      } else if (response.status === 200) {
        // Fallback - Redirect to Stripe Checkout for authentication
        const data = await response.json();
        if (data && data.url) {
          // Redirect to Stripe Checkout
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // Handle error responses
        const message = await response.text();

        if (response.status === 400) {
          alert(`${t('parkingPayment.validationError')}: ${message}`);
        } else if (response.status === 404) {
          alert(t('parkingPayment.accountNotFound'));
        } else if (response.status === 500) {
          alert(t('parkingPayment.serverError'));
        } else {
          alert(typeof message === 'string' ? message : t('parkingPayment.paymentError'));
        }
      }

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || t('parkingPayment.paymentError'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Format date
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (duration) => {
    if (duration === 0.5) return `30 ${t('parkingPayment.minutes')}`;
    if (duration === 1) return `1 ${t('parkingPayment.hour')}`;
    return `${duration} ${t('parkingPayment.hours')}`;
  };

  // Check if parking is active
  const isParkingActive = (parking) => {
    return parking.status === 'PAID' && new Date(parking.expiresAt) > new Date();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Form */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <CreditCardIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('parkingPayment.newPayment')}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('parkingPayment.completeDetails')}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handlePayment} className="p-2 space-y-6">
            {/* Car Selection - Single Dropdown */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('parkingPayment.selectVehicle')}
              </label>
              <FormControl fullWidth variant="outlined" size="small">
                {/* <InputLabel id="car-select-label"
                  className="dark:text-white">
                  {t('parkingPayment.selectVehicle', 'Selectează vehiculul')}
                </InputLabel> */}
                <Select
                  labelId="car-select-label"
                  id="car-select"
                  value={selectedCar?.id || ''}
                  onChange={(e) => {
                    const car = cars.find(c => c.id === e.target.value);
                    if (car) {
                      handleCarSelect(car);
                    }
                  }}
                  // label={t('parkingPayment.selectVehicle', 'Selectează vehiculul')}
                  className="bg-white dark:bg-slate-700 dark:text-white"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgb(148 163 184)', // slate-400
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgb(168 85 247)', // purple-500
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgb(168 85 247)', // purple-500
                    },
                    '& .MuiSelect-select': {
                      color: 'inherit',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'inherit',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgb(51 65 85)', // slate-700 for dark mode
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          backgroundColor: 'rgb(51 65 85)', // slate-700
                          '&:hover': {
                            backgroundColor: 'rgb(71 85 105)', // slate-600
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgb(168 85 247)', // purple-500
                            '&:hover': {
                              backgroundColor: 'rgb(147 51 234)', // purple-600
                            },
                          },
                        },
                      },
                    },
                  }}
                  required
                >
                  {cars.map((car) => (
                    <MenuItem key={car.id} value={car.id}>
                      <div className="flex justify-between items-center w-full">
                        <span>{car.make} {car.model}</span>
                        <span className="text-xs text-slate-500 ml-2">{car.plate}</span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Location Information */}
            <div>
              <label className="flex items-center justify-between text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4" />
                  {t('parkingPayment.yourLocation')}
                </span>
                {userLocation && (
                  <button
                    type="button"
                    onClick={getUserLocation}
                    className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    {t('parkingPayment.refreshLocation')}
                  </button>
                )}
              </label>
              {isLoadingLocation ? (
                <div className="flex items-center space-x-2 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700">
                  <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                  <span className="text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.detectingLocation')}</span>
                </div>
              ) : locationCity && !useManualCity ? (
                <div className="px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{locationCity}</p>
                      {locationStreet && (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.street')}: {locationStreet}</p>

                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseManualCity(true)}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:underline whitespace-nowrap"
                    >
                      {t('parkingPayment.changeCity')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {loadingCities ? (
                    <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                      <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.loadingCities')}</span>
                    </div>
                  ) : (
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="car-select-label"
                        className="dark:text-white">
                        {t('parkingPayment.selectCity', 'Selectează orașul')}
                      </InputLabel>
                      <Select
                        labelId="city-label"
                        id="city"
                        value={selectedCity}
                        onChange={(e) => {
                          setSelectedCity(e.target.value);
                          setUseManualCity(true);
                          setDetectedZone(null); // Clear auto-detection when manually selecting city
                        }}
                        label={t('parkingPayment.selectCity', 'Selectează orașul')}
                        className="bg-white dark:bg-slate-700 dark:text-white"
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgb(148 163 184)', // slate-400
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgb(168 85 247)', // purple-500
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgb(168 85 247)', // purple-500
                          },
                          '& .MuiSelect-select': {
                            color: 'inherit',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'inherit',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: 'rgb(51 65 85)', // slate-700 for dark mode
                              '& .MuiMenuItem-root': {
                                color: 'white',
                                backgroundColor: 'rgb(51 65 85)', // slate-700
                                '&:hover': {
                                  backgroundColor: 'rgb(71 85 105)', // slate-600
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgb(168 85 247)', // purple-500
                                  '&:hover': {
                                    backgroundColor: 'rgb(147 51 234)', // purple-600
                                  },
                                },
                              },
                            },
                          },
                        }}
                        required
                      >
                        {availableCities.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>
              )}
            </div>

            {/* Parking Zone */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('parkingPayment.parkingZone')}
                {detectedZone && (
                  <span className="ml-2 text-xs text-purple-600 dark:text-purple-400">
                    ({t('parkingPayment.autoDetected')})
                  </span>
                )}
              </label>
              {loadingZones ? (
                <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.loadingZones')}</span>
                </div>
              ) : parkingZones.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {getUniqueZones().map((z) => (
                    <button
                      key={z.zoneName}
                      type="button"
                      onClick={() => {
                        setZone(z.zoneName);
                        // Auto-select first duration for the selected zone
                        const durations = getDurationsForZone(z.zoneName);
                        if (durations.length > 0) {
                          setSelectedDuration(durations[0].zoneId);
                        }
                      }}
                      disabled={!selectedCity}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors border ${zone === z.zoneName
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {z.zoneName}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg text-center">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedCity ? t('parkingPayment.noZonesAvailable') : t('parkingPayment.selectCityFirst')}
                  </p>
                </div>
              )}
            </div>

            {/* Duration - Button selection */}
            {zone && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('parkingPayment.duration', 'Durată')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {getDurationsForZone(zone).map((z) => (
                    <button
                      key={z.zoneId}
                      type="button"
                      onClick={() => setSelectedDuration(z.zoneId)}
                      className={`p-3 rounded-lg font-medium transition-colors border text-center ${selectedDuration === z.zoneId
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      <div className="text-sm font-semibold">
                        {z.timeInMinutes} min
                        {parseFloat(z.timeInMinutes) >= 60 &&
                          ` (${(parseFloat(z.timeInMinutes) / 60).toFixed(1)} ${parseFloat(z.timeInMinutes) === 60 ? 'oră' : 'ore'})`
                        }
                      </div>
                      <div className="text-xs opacity-75">
                        {paymentMethod === 'balance'
                          ? (typeof z.userPriceWithAccount === 'number' ? z.userPriceWithAccount.toFixed(2) : z.userPriceWithAccount)
                          : (typeof z.userPrice === 'number' ? z.userPrice.toFixed(2) : z.userPrice)
                        } {z.currency}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('parkingPayment.paymentMethod')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('balance')}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${paymentMethod === 'balance'
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                    }`}
                >
                  <WalletIcon className="h-6 w-6 mb-1 text-purple-600 dark:text-purple-400" />
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('parkingPayment.balance', 'Sold')}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${paymentMethod === 'card'
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                    }`}
                >
                  <CreditCardIcon className="h-6 w-6 mb-1 text-purple-600 dark:text-purple-400" />
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('parkingPayment.card')}
                  </p>
                </button>
              </div>
            </div>

            {/* Balance Display */}
            {paymentMethod === 'balance' && driverAccount && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg px-4 py-3 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <WalletIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('parkingPayment.availableBalance')}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">
                    {driverAccount.balance ? driverAccount.balance.toFixed(2) : '0.00'} RON
                  </span>
                </div>
                {zone && selectedDuration && selectedZoneData && calculatePrice() > (driverAccount.balance || 0) && (
                  <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                    ⚠️ {t('parkingPayment.insufficientBalance')}
                  </div>
                )}
              </div>
            )}

            {/* Saved Payment Methods (only when card is selected) */}
            {paymentMethod === 'card' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('parkingPayment.selectPaymentMethod')}
                </label>
                {loadingPaymentMethods ? (
                  <div className="flex items-center justify-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <div className="animate-spin h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                    <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.loadingPaymentMethods')}</span>
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <div className="space-y-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setSelectedPaymentMethodId(method.id)}
                        className={`w-full p-3 rounded-lg border-2 text-left flex items-center justify-between ${selectedPaymentMethodId === method.id
                          ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <CreditCardIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white capitalize">
                              {method.brand} •••• {method.last4}
                            </p>
                            {method.expMonth && method.expYear && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {t('parkingPayment.expires')}: {String(method.expMonth).padStart(2, '0')}/{method.expYear}
                              </p>
                            )}
                          </div>
                        </div>
                        {selectedPaymentMethodId === method.id && (
                          <CheckCircleIcon className="h-6 w-6 text-purple-600" />
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPaymentMethodId(null);
                        setSaveForFutureUse(true);
                      }}
                      className={`w-full p-3 rounded-lg border-2 border-dashed text-left flex items-center justify-center space-x-2 ${selectedPaymentMethodId === null
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/30'
                        : 'border-slate-300 dark:border-slate-600 hover:border-purple-400'
                        }`}
                    >
                      <PlusCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                        {t('parkingPayment.addNewCard')}
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center">
                      <CreditCardIcon className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {t('parkingPayment.noSavedCards')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 px-2">
                      <input
                        type="checkbox"
                        id="saveForFutureUse"
                        checked={saveForFutureUse}
                        onChange={(e) => setSaveForFutureUse(e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="saveForFutureUse" className="text-sm text-slate-700 dark:text-slate-300">
                        {t('parkingPayment.saveCardForFuture')}
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Price Summary */}
            {zone && selectedDuration && selectedZoneData && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                  <span>{t('parkingPayment.zone')}</span>
                  <span className="font-semibold">{selectedZoneData.zoneName}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                  <span>{t('parkingPayment.duration')}</span>
                  <span className="font-semibold">
                    {selectedZoneData.timeInMinutes} {t('parkingPayment.minutes')}
                    {parseFloat(selectedZoneData.timeInMinutes) >= 60 &&
                      ` (${(parseFloat(selectedZoneData.timeInMinutes) / 60).toFixed(1)} ${parseFloat(selectedZoneData.timeInMinutes) === 60 ? t('parkingPayment.hour') : t('parkingPayment.hours')})`
                    }
                  </span>
                </div>

                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-1">
                  <div className="flex justify-between items-center text-sm text-slate-600 dark:text-slate-400">
                    <span>{t('parkingPayment.parkingCost')}</span>
                    <span>
                      {paymentMethod === 'balance'
                        ? (parseFloat(String(selectedZoneData.userPriceWithAccount).replace(',', '.')) || 0).toFixed(2)
                        : (parseFloat(String(selectedZoneData.userPriceWithAccount).replace(',', '.')) || 0).toFixed(2)
                      } {selectedZoneData.currency}
                    </span>
                  </div>
                  {paymentMethod === 'card' && calculateProcessingFee() > 0 && (
                    <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-500">
                      <span>{t('parkingPayment.processingFee')}</span>
                      <span>{calculateProcessingFee().toFixed(2)} {selectedZoneData.currency}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t-2 border-slate-300 dark:border-slate-600">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700 dark:text-slate-300 font-medium">
                      {t('parkingPayment.totalPrice')}
                    </span>
                    <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {calculatePrice().toFixed(2)} {selectedZoneData.currency}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isProcessing || !plateNumber || !zone || !selectedCity || !selectedDuration}
              className={`w-full py-3 rounded-lg font-semibold text-white  ${isProcessing || !plateNumber || !zone || !selectedCity || !selectedDuration
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg'
                }`}
            >
              {isProcessing ? t('parkingPayment.processing') : t('parkingPayment.payNow')}
            </button>
          </form>
        </div>

        {/* Parking History & Active Sessions */}
        <div className="space-y-6">
          {/* Active Parking Sessions */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <ClockIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {t('parkingPayment.activeSessions')}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t('parkingPayment.activeSessionsCount')} ({parkingHistory.filter(p => isParkingActive(p)).length})
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              {parkingHistory.filter(p => isParkingActive(p)).length > 0 ? (
                <div className="space-y-3">
                  {parkingHistory
                    .filter(p => isParkingActive(p))
                    .map((parking) => (
                      <div
                        key={parking.id}
                        className="p-4 rounded-lg border-2 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">
                              {parking.plateNumber}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {parking.displayDetails} • {parking.zone}
                            </p>
                            {parking.city && (
                              <p className="text-xs text-slate-500 dark:text-slate-500">
                                {parking.city}
                              </p>
                            )}
                          </div>
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full">
                            {t('parkingPayment.active')}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 space-x-4">
                          <div className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {formatDuration(parking.duration)}
                          </div>
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {t('parkingPayment.expiresAt')}: {formatDateTime(parking.expiresAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPinIcon className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {t('parkingPayment.noActiveSessions')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment History */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="px-2 py-3 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {t('parkingPayment.parkingHistory')}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t('parkingPayment.parkingHistorySubtitle')}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-2 py-2">
              {paymentHistoryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">{t('parkingPayment.loadingParkingHistory')}</span>
                </div>
              ) : paymentHistoryError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 dark:text-red-400 mb-2">{paymentHistoryError}</p>
                  <button
                    onClick={() => fetchPaymentHistory(historyPage, historySize)}
                    className="text-purple-600 dark:text-purple-400 hover:underline text-sm"
                  >
                    {t('parkingPayment.tryAgain')}
                  </button>
                </div>
              ) : parkingHistory.length > 0 ? (
                <div className="space-y-3">
                  {parkingHistory.map((parking) => (
                    <div
                      key={parking.id}
                      className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-gray-700/50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-slate-900 dark:text-white">
                              {parking.plateNumber}
                            </p>
                            {/* Status Badge */}
                            {/* <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${parking.status === 'PAID'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : parking.status === 'ONGOING'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : parking.status === 'UNSUCCESSFUL'
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                  : 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
                              }`}>
                              {parking.status === 'PAID' ? 'Plătit' :
                                parking.status === 'ONGOING' ? 'În procesare' :
                                  parking.status === 'UNSUCCESSFUL' ? 'Eșuat' : parking.status}
                            </span> */}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {parking.displayDetails} • {parking.zone}
                            {parking.city && ` • ${parking.city}`}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                            <ClockIcon className="h-3 w-3 mr-1 inline" />
                            {formatDuration(parking.duration)}
                            {parking.status === 'ONGOING' && (
                              <span className="ml-2">
                                • {t('parkingPayment.expires')}: {formatDateTime(parking.expiresAt)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                        <span>{t('parkingPayment.created')}: {formatDateTime(parking.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {t('parkingPayment.noParkingSessions')}
                  </p>
                </div>
              )}

              {/* Pagination Controls */}
              {totalHistoryPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setHistoryPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={historyPage === 1 || paymentHistoryLoading}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${historyPage === 1 || paymentHistoryLoading
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      ← {t('parkingPayment.previous')}
                    </button>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {t('parkingPayment.page')} {historyPage}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setHistoryPage(prev => prev + 1);
                      }}
                      disabled={!hasMorePages || paymentHistoryLoading}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${!hasMorePages || paymentHistoryLoading
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      {t('parkingPayment.next')} →
                    </button>
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {t('parkingPayment.total')}: {totalHistoryElements} {t('parkingPayment.sessions')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-10 w-10 text-emerald-600 dark:text-green-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {t('parkingPayment.paymentSuccess')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {t('parkingPayment.paymentSuccessMessage')}
            </p>
            <button
              onClick={() => setShowSuccess(false)}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 "
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Invoice Data Modal */}
      {showInvoiceModal && (
        <InvoiceDataModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          onSuccess={async () => {
            await refreshDriverAccount();
            setShowInvoiceModal(false);
            // Continue with the parking payment after invoice data is saved
          }}
          driverAccount={driverAccount}
        />
      )}
    </div>
  );
};

export default ParkingPayment;
